import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Calendar, Users, DollarSign, TrendingUp, Plus, Pencil, Trash2, CheckCircle, XCircle, Upload, Image, MessageSquare, Bell, Send, Phone } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending_payment: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-700",
  approved: "bg-blue-100 text-blue-700",
  completed: "bg-[#1A2F24] text-white",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

const CATEGORIES = ["Trailers", "Generators", "Tractors", "Tractor Attachments", "Lawn Care"];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-[#595959] uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "", category: "Trailers", description: "", daily_rate: 0, weekly_rate: 0,
    deposit: 0, capacity: "", details: "", image_url: "", available: true
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file, target) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload/equipment-image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      if (target === "new") {
        setNewItem(prev => ({ ...prev, image_url: imageUrl }));
      } else {
        setEditItem(prev => ({ ...prev, image_url: imageUrl }));
      }
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const [smsConfig, setSmsConfig] = useState(null);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, equipRes, bookRes, smsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/equipment`, { withCredentials: true }),
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/sms-config`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setEquipment(equipRes.data);
      setBookings(bookRes.data);
      if (smsRes.data) setSmsConfig(smsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-reminders`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendCustomSms = async () => {
    if (!smsPhone || !smsMessage) {
      toast.error("Enter phone number and message");
      return;
    }
    setSendingSms(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-sms`,
        { phone: smsPhone, message: smsMessage },
        { withCredentials: true }
      );
      if (data.sent) {
        toast.success("SMS sent!");
        setSmsPhone("");
        setSmsMessage("");
      } else {
        toast.error("SMS failed to send");
      }
    } catch (err) {
      toast.error("Failed to send SMS");
    } finally {
      setSendingSms(false);
    }
  };

  const handleAddEquipment = async () => {
    try {
      await axios.post(`${API}/equipment`, {
        ...newItem,
        daily_rate: parseFloat(newItem.daily_rate),
        weekly_rate: parseFloat(newItem.weekly_rate),
        deposit: parseFloat(newItem.deposit),
      }, { withCredentials: true });
      toast.success("Equipment added!");
      setAddDialog(false);
      setNewItem({ name: "", category: "Trailers", description: "", daily_rate: 0, weekly_rate: 0, deposit: 0, capacity: "", details: "", image_url: "", available: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const handleUpdateEquipment = async () => {
    if (!editItem) return;
    try {
      await axios.put(`${API}/equipment/${editItem.id}`, {
        name: editItem.name,
        category: editItem.category,
        description: editItem.description,
        daily_rate: parseFloat(editItem.daily_rate),
        weekly_rate: parseFloat(editItem.weekly_rate),
        deposit: parseFloat(editItem.deposit),
        capacity: editItem.capacity,
        details: editItem.details,
        image_url: editItem.image_url,
        available: editItem.available,
      }, { withCredentials: true });
      toast.success("Equipment updated!");
      setEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    try {
      await axios.delete(`${API}/equipment/${id}`, { withCredentials: true });
      toast.success("Equipment deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status }, { withCredentials: true });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update booking");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="w-8 h-8 border-4 border-[#E68A00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F8F6]">
      <div className="bg-[#1A2F24] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Admin Dashboard
          </h1>
          <p className="text-white/60 text-sm mt-1">Manage your equipment and bookings</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={Package} label="Equipment" value={stats.total_equipment} color="bg-[#E68A00]/10 text-[#E68A00]" />
            <StatCard icon={Calendar} label="Total Bookings" value={stats.total_bookings} color="bg-[#1A2F24]/10 text-[#1A2F24]" />
            <StatCard icon={Users} label="Customers" value={stats.total_customers} color="bg-blue-50 text-blue-600" />
            <StatCard icon={DollarSign} label="Revenue" value={`$${stats.total_revenue?.toFixed(2)}`} color="bg-green-50 text-green-600" />
          </div>
        )}

        <Tabs defaultValue="equipment" className="w-full">
          <TabsList className="bg-[#EFECE5] mb-6">
            <TabsTrigger value="equipment" data-testid="tab-equipment">Equipment</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <MessageSquare className="w-4 h-4 mr-1" /> SMS
            </TabsTrigger>
          </TabsList>

          {/* EQUIPMENT TAB */}
          <TabsContent value="equipment">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Equipment Inventory
              </h2>
              <button
                data-testid="add-equipment-btn"
                onClick={() => setAddDialog(true)}
                className="inline-flex items-center gap-2 bg-[#E68A00] hover:bg-[#CC7A00] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Equipment
              </button>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="equipment-table">
                  <thead>
                    <tr className="bg-[#F9F8F6] border-b border-[#E5E0D8]">
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Category</th>
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Daily</th>
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Weekly</th>
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Deposit</th>
                      <th className="text-left py-3 px-4 font-medium text-[#595959]">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-[#595959]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.map(item => (
                      <tr key={item.id} className="border-b border-[#E5E0D8] last:border-0 hover:bg-[#F9F8F6]">
                        <td className="py-3 px-4 font-medium text-[#1C1C1C]">{item.name}</td>
                        <td className="py-3 px-4 text-[#595959]">{item.category}</td>
                        <td className="py-3 px-4 text-[#595959]">${item.daily_rate}</td>
                        <td className="py-3 px-4 text-[#595959]">${item.weekly_rate}</td>
                        <td className="py-3 px-4 text-[#595959]">${item.deposit}</td>
                        <td className="py-3 px-4">
                          <Badge className={item.available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                            {item.available ? "Available" : "Unavailable"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            data-testid={`edit-equipment-${item.id}`}
                            onClick={() => { setEditItem({ ...item }); setEditDialog(true); }}
                            className="text-[#595959] hover:text-[#E68A00] p-1 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`delete-equipment-${item.id}`}
                            onClick={() => handleDeleteEquipment(item.id)}
                            className="text-[#595959] hover:text-red-600 p-1 ml-2 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* BOOKINGS TAB */}
          <TabsContent value="bookings">
            <h2 className="text-xl font-semibold text-[#1A2F24] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              All Bookings
            </h2>
            {bookings.length === 0 ? (
              <p className="text-center text-[#595959] py-12">No bookings yet.</p>
            ) : (
              <div className="space-y-3">
                {bookings.map(booking => (
                  <div
                    key={booking.id}
                    data-testid={`admin-booking-${booking.id}`}
                    className="bg-white border border-[#E5E0D8] rounded-lg p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-[#1C1C1C]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {booking.equipment_name}
                          </h4>
                          <Badge className={STATUS_STYLES[booking.status] || "bg-gray-100"}>
                            {booking.status?.replace("_", " ")}
                          </Badge>
                          <Badge className={booking.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}>
                            {booking.payment_status}
                          </Badge>
                        </div>
                        <div className="text-sm text-[#595959] space-y-0.5">
                          <p>Customer: {booking.user_name} ({booking.user_email})</p>
                          <p>Dates: {booking.start_date} &rarr; {booking.end_date} &middot; {booking.rental_type} &middot; ${booking.total?.toFixed(2)}</p>
                          {booking.notes && <p>Notes: {booking.notes}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {booking.status !== "completed" && booking.status !== "rejected" && (
                          <>
                            <button
                              data-testid={`approve-booking-${booking.id}`}
                              onClick={() => handleBookingStatus(booking.id, "approved")}
                              className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button
                              data-testid={`reject-booking-${booking.id}`}
                              onClick={() => handleBookingStatus(booking.id, "rejected")}
                              className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                            <button
                              data-testid={`complete-booking-${booking.id}`}
                              onClick={() => handleBookingStatus(booking.id, "completed")}
                              className="inline-flex items-center gap-1 bg-[#1A2F24] hover:bg-[#264233] text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                            >
                              Complete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications">
            <div className="space-y-6">
              {/* SMS Status */}
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#E68A00]/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#E68A00]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Twilio SMS Notifications
                    </h3>
                    <p className="text-sm text-[#595959]">
                      {smsConfig?.twilio_configured ? (
                        <span className="text-green-600 font-medium">Connected</span>
                      ) : (
                        <span className="text-red-600 font-medium">Not configured</span>
                      )}
                      {smsConfig?.twilio_phone && ` \u2014 Sending from ${smsConfig.twilio_phone}`}
                    </p>
                  </div>
                </div>

                {smsConfig && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#F9F8F6] border border-[#E5E0D8] rounded-md p-4">
                      <h4 className="text-sm font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Automatic Notifications
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-[#595959]">Booking confirmed (after payment)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-[#595959]">Booking approved by admin</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-[#E68A00]" />
                          <span className="text-[#595959]">Rental reminders (manual trigger)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-[#595959]">Admin alerts for new bookings</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#F9F8F6] border border-[#E5E0D8] rounded-md p-4">
                      <h4 className="text-sm font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Admin Alert Recipients
                      </h4>
                      <div className="space-y-1.5">
                        {smsConfig.admin_phones?.map((phone, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-[#595959]">
                            <Phone className="w-3.5 h-3.5 text-[#E68A00]" />
                            {phone}
                            <span className="text-xs text-[#595959]/60">{i === 0 ? '(Keara)' : '(Dallas)'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Send Reminders */}
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Send Pickup Reminders
                </h3>
                <p className="text-sm text-[#595959] mb-4">
                  Send SMS reminders to all customers with confirmed bookings starting tomorrow.
                </p>
                <button
                  data-testid="send-reminders-btn"
                  onClick={handleSendReminders}
                  disabled={sendingReminders}
                  className="inline-flex items-center gap-2 bg-[#1A2F24] hover:bg-[#264233] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors"
                >
                  <Bell className="w-4 h-4" />
                  {sendingReminders ? "Sending..." : "Send Tomorrow's Reminders"}
                </button>
              </div>

              {/* Custom SMS */}
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Send Custom SMS
                </h3>
                <p className="text-sm text-[#595959] mb-4">
                  Send a quick text to any customer.
                </p>
                <div className="space-y-3 max-w-md">
                  <div>
                    <Label className="text-[#1C1C1C]">Phone Number</Label>
                    <Input
                      data-testid="custom-sms-phone"
                      value={smsPhone}
                      onChange={(e) => setSmsPhone(e.target.value)}
                      placeholder="+12815551234"
                      className="mt-1 bg-white border-[#E5E0D8]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1C1C1C]">Message</Label>
                    <textarea
                      data-testid="custom-sms-message"
                      value={smsMessage}
                      onChange={(e) => setSmsMessage(e.target.value)}
                      placeholder="Your message..."
                      className="w-full mt-1 p-3 border border-[#E5E0D8] rounded-md text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#E68A00]"
                    />
                  </div>
                  <button
                    data-testid="send-custom-sms-btn"
                    onClick={handleSendCustomSms}
                    disabled={sendingSms}
                    className="inline-flex items-center gap-2 bg-[#E68A00] hover:bg-[#CC7A00] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {sendingSms ? "Sending..." : "Send SMS"}
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ADD EQUIPMENT DIALOG */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Add Equipment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#1C1C1C]">Name</Label>
              <Input data-testid="add-equip-name" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label className="text-[#1C1C1C]">Category</Label>
              <Select value={newItem.category} onValueChange={v => setNewItem({...newItem, category: v})}>
                <SelectTrigger className="mt-1" data-testid="add-equip-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#1C1C1C]">Description</Label>
              <textarea data-testid="add-equip-desc" value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} className="w-full mt-1 p-2 border border-[#E5E0D8] rounded-md text-sm h-16 resize-none" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[#1C1C1C]">Daily Rate ($)</Label>
                <Input data-testid="add-equip-daily" type="number" step="0.01" value={newItem.daily_rate} onChange={e => setNewItem({...newItem, daily_rate: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Weekly Rate ($)</Label>
                <Input data-testid="add-equip-weekly" type="number" step="0.01" value={newItem.weekly_rate} onChange={e => setNewItem({...newItem, weekly_rate: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Deposit ($)</Label>
                <Input data-testid="add-equip-deposit" type="number" step="0.01" value={newItem.deposit} onChange={e => setNewItem({...newItem, deposit: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-[#1C1C1C]">Capacity</Label>
              <Input data-testid="add-equip-capacity" value={newItem.capacity} onChange={e => setNewItem({...newItem, capacity: e.target.value})} className="mt-1" placeholder="e.g. 3500 lbs" />
            </div>
            <div>
              <Label className="text-[#1C1C1C]">Equipment Image</Label>
              <div className="mt-1 space-y-2">
                <label
                  data-testid="add-equip-upload"
                  className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-[#E5E0D8] rounded-md p-4 cursor-pointer hover:border-[#E68A00] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <Upload className="w-5 h-5 text-[#595959]" />
                  <span className="text-sm text-[#595959]">{uploading ? "Uploading..." : "Upload Image"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], "new")}
                  />
                </label>
                {newItem.image_url && (
                  <div className="flex items-center gap-2">
                    <img src={newItem.image_url} alt="Preview" className="w-16 h-16 object-cover rounded border border-[#E5E0D8]" />
                    <span className="text-xs text-green-600 flex items-center gap-1"><Image className="w-3 h-3" /> Uploaded</span>
                  </div>
                )}
                <p className="text-xs text-[#595959]">Or paste an image URL:</p>
                <Input data-testid="add-equip-image" value={newItem.image_url} onChange={e => setNewItem({...newItem, image_url: e.target.value})} placeholder="https://..." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddDialog(false)} className="px-4 py-2 text-sm text-[#595959] hover:text-[#1C1C1C]">Cancel</button>
            <button data-testid="add-equip-submit" onClick={handleAddEquipment} className="bg-[#E68A00] hover:bg-[#CC7A00] text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors">
              Add Equipment
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT EQUIPMENT DIALOG */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Equipment</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <div>
                <Label className="text-[#1C1C1C]">Name</Label>
                <Input data-testid="edit-equip-name" value={editItem.name} onChange={e => setEditItem({...editItem, name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Category</Label>
                <Select value={editItem.category} onValueChange={v => setEditItem({...editItem, category: v})}>
                  <SelectTrigger className="mt-1" data-testid="edit-equip-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[#1C1C1C]">Daily Rate ($)</Label>
                  <Input data-testid="edit-equip-daily" type="number" step="0.01" value={editItem.daily_rate} onChange={e => setEditItem({...editItem, daily_rate: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[#1C1C1C]">Weekly Rate ($)</Label>
                  <Input data-testid="edit-equip-weekly" type="number" step="0.01" value={editItem.weekly_rate} onChange={e => setEditItem({...editItem, weekly_rate: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[#1C1C1C]">Deposit ($)</Label>
                  <Input data-testid="edit-equip-deposit" type="number" step="0.01" value={editItem.deposit} onChange={e => setEditItem({...editItem, deposit: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[#1C1C1C]">Available</Label>
                <button
                  data-testid="edit-equip-available"
                  onClick={() => setEditItem({...editItem, available: !editItem.available})}
                  className={`w-10 h-6 rounded-full transition-colors ${editItem.available ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${editItem.available ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Equipment Image</Label>
                <div className="mt-1 space-y-2">
                  <label
                    data-testid="edit-equip-upload"
                    className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-[#E5E0D8] rounded-md p-3 cursor-pointer hover:border-[#E68A00] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <Upload className="w-4 h-4 text-[#595959]" />
                    <span className="text-sm text-[#595959]">{uploading ? "Uploading..." : "Upload New Image"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], "edit")}
                    />
                  </label>
                  {editItem.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={editItem.image_url} alt="Current" className="w-16 h-16 object-cover rounded border border-[#E5E0D8]" />
                      <span className="text-xs text-[#595959]">Current image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditDialog(false)} className="px-4 py-2 text-sm text-[#595959]">Cancel</button>
            <button data-testid="edit-equip-submit" onClick={handleUpdateEquipment} className="bg-[#E68A00] hover:bg-[#CC7A00] text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors">
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
